import { useContext, useEffect, useRef, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Family } from "protogen/advisors_service_pb";
import { GetAdvisorActivityFeedResponse } from "protogen/feed_service_pb";
import { FeedEntry, Medium, FeedEntryType } from "protogen/feed_pb";
import { Member } from "protogen/common_pb";
import CompositionSection from "./CompositionSection";
import { CurrentUserContext } from "../context/RequireAuth";
import { FeedFocusState } from "../../types/feed";
import { ScrollContainerHandle } from "../common/ScrollContainer";
import AttachmentDialog from "../common/AttachmentDialog";
import { useGetTextMessageEntry } from "services/advisor";
import { useGetAdvisorActivityFeed } from "services/feed";
import useIsVisible from "../hooks/useIsVisible";
import ContactSection from "./ContactSection";
import SuggestContactCard from "./SuggestContactCard";
import ScrollableActivityPanel from "../details-page/ScrollableActivityPanel";
import {
  Attachment,
  AccountStub,
  UserPermission,
  Advisor,
} from "protogen/common_pb";
import FeedEntryComponent from "./FeedEntryComponent";
import { dedupeFeedEntries, sortEntryTimestamps } from "./utils";
import { insertTaskSuggestion, SuggestionActionsType } from "./Suggestions";
import CenteredEntryFooter from "./CenteredEntryFooter";
import { usePushContext } from "../context/PushContextProvider";
import BookmarkToTaskDialog, {
  fromFeedEntry,
} from "../tasks/BookmarkToTaskDialog";
import { useRetrySendTextMessage } from "services/textMessage";
import ActivityFeedFamilyStateWarning from "./ActivityFeedFamilyStateWarning";
import useIsMobile from "components/hooks/useIsMobile";
import { Box } from "@mui/material";
import { ActivityFeedFilterContext } from "components/context/ActivityFeedFilterContext";
import { SearchContext } from "components/context/SearchContextProvider";
import { useWebSocketContext } from "components/context/SocketConsumerProvider";
import { StatusContext } from "components/context/StatusContextProvider";
import useCached from "../hooks/useCached";

const determineMostRecentMemberContacted = (
  members: Member[],
  entries: FeedEntry[],
): [Member | null, string[] | null] => {
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    let memberContacted: Member | null = null;
    members.forEach((m) => {
      if (entry.senderRef === m.ref || entry.recipientRefs.includes(m.ref)) {
        memberContacted = m;
      }
    });
    if (memberContacted) {
      // recipientRefs is the conversation's full participant list, so it addresses the exact
      // thread the message arrived on - including groups with an external contact.
      const isGroup = entry.recipientRefs.length > 1;
      return [memberContacted, isGroup ? entry.recipientRefs : null];
    }
  }
  return [null, null];
};

const useSMSRetry = () => {
  const { request } = useRetrySendTextMessage();
  const handleRetry = async (messageRef: string) => {
    const response = await request({ messageRef });
    if (!response) {
      return null;
    }
    const message = response.textMessage;
    return new FeedEntry({
      ref: message.ref,
      senderRef: message.senderRef,
      timestampSec: message.timestampSec,
      medium: Medium.SMS,
      textMessage: message,
      recipientRefs: [],
    });
  };
  return { handleRetry };
};

type Props = {
  family: Family;
  suggestionActions: SuggestionActionsType;
  primaryAdvisor: Advisor;
  advisorPermissions: UserPermission[];
  selectedMedium: Medium | null;
  communicationCallbacks?: {
    onTextMessageSent: (e: FeedEntry) => void;
    onEmailSent: (e: FeedEntry) => void;
    onPhoneCallStarted: (e: FeedEntry) => void;
  };
};

export default ({
  family,
  suggestionActions,
  primaryAdvisor,
  advisorPermissions,
  selectedMedium,
  communicationCallbacks,
}: Props) => {
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activityRef, setActivityRef] = useState<string | null>(
    searchParams.get("activityRef"),
  );
  const {
    registerHandler: registerWebSocketHandler,
    unregisterHandler: unregisterWebSocketHandler,
  } = useWebSocketContext();
  const { refresh: statusRefresh } = useContext(StatusContext);
  const [isCenteredEntry, setIsCenteredEntry] = useState(activityRef !== null);
  const currentUser = useContext(CurrentUserContext);
  const { registerHandler, unregisterHandler } = usePushContext();
  const hasLoadedOnce = useRef(false);
  const hasEntryTypeChanged = useRef(false);
  const requestId = useRef(0);

  const [cachedRecipient, setCachedRecipient] = useCached<{
    selectedContactRef: string;
    groupText: boolean;
    groupRecipientRefs: string[];
  } | null>(`activity-feed-recipient:${family.ref}`, null);

  const getInitialContact = (): Member => {
    if (cachedRecipient) {
      const cachedContact = family.familyMembers.find(
        (m) => m.ref === cachedRecipient.selectedContactRef,
      );
      if (cachedContact) {
        return cachedContact;
      }
    }
    return family.familyMembers[0];
  };

  const [feedFocusState, setFeedFocusState] = useState<FeedFocusState>({
    selectedContact: getInitialContact(),
    selectedMedium: null,
    selectedEntryKey: null,
    replyToEmail: null,
    editEmailDraft: null,
    isDirty: !!cachedRecipient,
    groupText: cachedRecipient?.groupText ?? false,
    groupRecipientRefs: cachedRecipient?.groupRecipientRefs ?? [],
  });
  const { handleRetry } = useSMSRetry();
  const scrollRef = useRef<ScrollContainerHandle | null>(null);
  const [localEntries, setLocalEntries] = useState<FeedEntry[]>([]);
  const [feedCursor, setFeedCursor] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [entries, setEntries] = useState<FeedEntry[]>([]);
  const [serverGroupThreads, setServerGroupThreads] = useState<string[][]>([]);
  const applyGroupThreads = (resp: GetAdvisorActivityFeedResponse) => {
    if (resp.groupThreads.length === 0) {
      return;
    }
    setServerGroupThreads(
      resp.groupThreads
        .map((t) => t.memberRefs)
        .filter((refs) => refs.length >= 2),
    );
  };
  const [accountMap, setAccountMap] = useState<Record<string, AccountStub>>({
    [currentUser.ref]: currentUser,
    ...family.familyMembers.reduce((acc, m) => ({ ...acc, [m.ref]: m }), {}),
  });
  // The server list is member-only, so group threads with an external contact can only be
  // recovered from the loaded page. Entries are newest-first, so recently used threads lead.
  const groupThreads = useMemo<string[][]>(() => {
    const threads: string[][] = [];
    const seen = new Set<string>();
    const add = (refs: string[]) => {
      const key = [...refs].sort().join("|");
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      threads.push(refs);
    };
    for (const entry of entries) {
      if (
        entry.medium !== Medium.SMS ||
        entry.recipientRefs.length < 2 ||
        // A cross-advisor thread lists the advisor as a participant; never offer texting yourself.
        entry.recipientRefs.includes(currentUser.ref) ||
        !entry.recipientRefs.every((r) => accountMap[r])
      ) {
        continue;
      }
      add(entry.recipientRefs);
    }
    serverGroupThreads.forEach(add);
    return threads;
  }, [entries, accountMap, serverGroupThreads, currentUser.ref]);
  const [openAttachment, setOpenAttachment] = useState<Attachment | null>(null);
  const [bookmarkEntry, setBookmarkEntry] = useState<FeedEntry | null>(null);
  const { entryType } = useContext(ActivityFeedFilterContext);
  const { setPhoneCallsOnly, setTasksOnly, setTextsOnly } =
    useContext(SearchContext);
  const updateEntries = (
    r: GetAdvisorActivityFeedResponse,
    currentRequestId: number,
  ) => {
    if (currentRequestId !== requestId.current) {
      return;
    }

    hasLoadedOnce.current = true;
    setEntries((pastEntries) => {
      return dedupeFeedEntries(
        [...r.entries, ...pastEntries].sort(sortEntryTimestamps),
      );
    });
    setAccountMap((pastMap) => {
      return {
        ...pastMap,
        ...r.accounts.reduce(
          (acc: Record<string, AccountStub>, account: AccountStub) => {
            acc[account.ref] = account;
            return acc;
          },
          {},
        ),
      };
    });
    if (!r.hasMore) {
      // End of the line, currently you can't get out of this state
      setHasMore(false);
    }
    // Don't blow away a good cursor on a refresh If the prev request used a cursor update the next one OR if
    // we haven't set one yet.
    if ((r.startCursor || !feedCursor) && feedCursor !== r.nextCursor) {
      setFeedCursor(() => r.nextCursor);
    }
  };
  const { request } = useGetAdvisorActivityFeed();
  const { request: fetchMessageEntry } = useGetTextMessageEntry(async (r) => {
    const response = await request({
      familyRef: family.ref,
      startCursor: `timestampSec=${BigInt(r.entry?.timestampSec ?? 0)}`,
      markConversationsRead: false,
      pageSize: 50,
      direction: "asc",
      entryTypes: [entryType],
      includeGroupThreads: true,
    });
    if (response) {
      updateEntries(response, requestId.current);
      applyGroupThreads(response);
    }
    setHasMore(true);
    const resp = await request({
      familyRef: family.ref,
      startCursor: `timestampSec=${
        BigInt(r.entry?.timestampSec ?? 0) + BigInt(1)
      }`,
      markConversationsRead: false,
      pageSize: 10,
      direction: "desc",
      entryTypes: [entryType],
      includeGroupThreads: false,
    });
    if (resp) {
      updateEntries(resp, requestId.current);
    }
  });

  const refresh = async () => {
    const currentRequestId = ++requestId.current;
    const resp = await request({
      familyRef: family.ref,
      startCursor: "",
      // Only mark conversations as read if the feed is visible.
      markConversationsRead: isVisible,
      pageSize: 20,
      direction: "desc",
      entryTypes: [entryType],
      includeGroupThreads: true,
    });
    if (resp) {
      updateEntries(resp, currentRequestId);
      applyGroupThreads(resp);
      if (resp?.readStatusUpdated) {
        statusRefresh();
      }
    }
  };
  const { isVisible } = useIsVisible({
    onRefocus: async (blurSecs) => {
      if (blurSecs > 10) {
        await refresh();
      }
    },
  });
  useEffect(() => {
    setFeedFocusState({
      ...feedFocusState,
      selectedMedium: selectedMedium,
    });
  }, [selectedMedium]);
  useEffect(() => {
    // In centered-entry mode the feed is loaded around the activity instead; fetching the
    // latest entries here would splice recent items into the feed with a gap in between.
    if (!hasLoadedOnce.current && !isCenteredEntry) {
      // Something is wrong here - this is loading 2x more than it should because "feedCursor" is triggering itself.
      refresh();
    }
  }, [family.ref, isVisible, feedCursor, entryType]);

  useEffect(() => {
    // This handles the case where a family is updated and we may need to change the default
    // contact information.
    const updatedMembers = new Map(family.familyMembers.map((m) => [m.ref, m]));
    const selectedContact = updatedMembers.get(
      feedFocusState.selectedContact.ref,
    );
    if (selectedContact) {
      setFeedFocusState((prevState) => ({
        ...prevState,
        selectedContact: selectedContact,
      }));
    }

    // Until a user has made an explicit change (sent message, changed contact) lets update the selected contact form
    // to the latest contact who's been chatted with. This should help us avoid looking at a message from user A
    // but sending a response to user B.
    const [latestMember, groupRecipientRefs] =
      determineMostRecentMemberContacted(family.familyMembers, entries);
    if (latestMember) {
      // To make sure we have the latest state, use a callback to decide if we should update selectedContact.
      setFeedFocusState((prevState) => ({
        ...prevState,
        ...(!prevState.isDirty
          ? {
              selectedContact: latestMember,
              groupText: !!groupRecipientRefs,
              groupRecipientRefs: groupRecipientRefs ?? [],
            }
          : {}),
      }));
    }
  }, [entries, family]);

  useEffect(() => {
    // no need to run this if the entry type hasn't been set to anything
    if (!hasEntryTypeChanged.current && entryType === FeedEntryType.UNSPECIFIED)
      return;
    hasEntryTypeChanged.current = true;
    setEntries([]);
    setLocalEntries([]);
    refresh();
    setPhoneCallsOnly(entryType === FeedEntryType.PHONE_CALL);
    setTasksOnly(entryType === FeedEntryType.TASK_ACTIVITY);
    setTextsOnly(entryType === FeedEntryType.MESSAGE);
  }, [entryType]);

  useEffect(() => {
    const initialActivityRef = searchParams.get("activityRef");
    if (initialActivityRef == null) return;
    if (hasLoadedOnce.current && initialActivityRef !== null) {
      // Activity feed assumes certain state on load.  If we are on the family page and want to center
      // an entry, reload the page/component.  Not ideal, but not a huge hit.
      window.location.reload();
    }
    setActivityRef(initialActivityRef);
    setIsCenteredEntry(initialActivityRef !== null);
    // remove the activityRef so on refresh the user is removed from the "centered entry mode".
    searchParams.delete("activityRef");
    navigate("." + searchParams.toString(), { replace: true });
  }, [searchParams]);

  useEffect(() => {
    if (isCenteredEntry) {
      fetchMessageEntry({
        messageRef: activityRef || "",
      });
    }
  }, []);

  useEffect(() => {
    const pushHandler = () => {
      if (isVisible) {
        refresh();
      }
    };
    const channels = ["task/", "email/", "phonecall/", "textmessage/"];
    registerWebSocketHandler(channels, pushHandler);
    registerHandler(pushHandler);

    return () => {
      unregisterHandler(pushHandler);
      unregisterWebSocketHandler(channels, pushHandler);
    };
  }, [
    isVisible,
    registerHandler,
    unregisterHandler,
    registerWebSocketHandler,
    unregisterWebSocketHandler,
  ]);

  // Dedupe.
  const allEntries = dedupeFeedEntries(
    [...entries, ...localEntries].sort(sortEntryTimestamps),
  );
  const suggestContactCard =
    hasLoadedOnce.current &&
    !hasEntryTypeChanged.current &&
    allEntries.every((entry) => entry.medium !== Medium.SMS);

  const addFeedEntry = (entry: FeedEntry) => {
    setFeedFocusState((prevState) => ({
      ...prevState,
      isDirty: true,
      selectedMedium: Medium.SMS,
      selectedEntryKey: `${entry.medium}-${entry.ref}`,
    }));
    setLocalEntries([...localEntries, entry]);
    scrollRef.current?.setSticky();
    refresh();
  };
  const fetchMoreData = async () => {
    const resp = await request({
      familyRef: family.ref,
      startCursor: feedCursor,
      markConversationsRead: false,
      pageSize: 10,
      direction: "desc",
      entryTypes: [entryType],
      includeGroupThreads: false,
    });
    if (resp) {
      updateEntries(resp, requestId.current);
    }
  };

  const createFeedEntryComponent = (e: FeedEntry, i: number): JSX.Element => {
    const isSearchEntry = activityRef === e.ref;
    return (
      <FeedEntryComponent
        key={`${e.medium}-${e.ref}`}
        entry={e}
        feedFocusState={feedFocusState}
        setFeedFocusState={setFeedFocusState}
        accountMap={accountMap}
        openAttachment={(attachment) => {
          setOpenAttachment(attachment);
        }}
        // Be confused! We sort these in reverse from our CSS rendering which makes life more confusing.
        nextEntry={allEntries[i - 1]}
        prevEntry={allEntries[i + 1]}
        isCenteredEntry={isSearchEntry}
        onBookmark={() => setBookmarkEntry(e)}
        noAvatars={true}
        familyRef={family.ref}
        handleRetry={
          e.medium !== Medium.SMS
            ? undefined
            : async () => {
                const entry = await handleRetry(e.ref);
                if (entry) {
                  addFeedEntry(entry);
                }
              }
        }
        handleRefresh={refresh}
        handleRemove={() => {
          setLocalEntries((pastEntries) => {
            return pastEntries.filter(
              (x) => !(x.ref === e.ref && x.medium === e.medium),
            );
          });
          setEntries((pastEntries) => {
            return pastEntries.filter(
              (x) => !(x.ref === e.ref && x.medium === e.medium),
            );
          });
        }}
      />
    );
  };

  const scrollEntries = useMemo(() => {
    const allEntries = dedupeFeedEntries(
      [...entries, ...localEntries].sort(sortEntryTimestamps),
    );
    const entryEls = insertTaskSuggestion(
      allEntries,
      suggestionActions,
      createFeedEntryComponent,
      {},
      family.ref,
    );
    if (isCenteredEntry) {
      entryEls.unshift(<CenteredEntryFooter key="centered-entry-footer" />);
    }
    return entryEls;
  }, [entries, localEntries]);

  if (suggestContactCard) {
    scrollEntries.push(
      <SuggestContactCard
        key={"suggest-contact-card"}
        family={family}
        recipient={feedFocusState.selectedContact}
        onShared={addFeedEntry}
      />,
    );
  }

  const quickResponseSuggestions = useMemo(() => {
    if (entries.length > 0) {
      const lastEntry = entries[0];
      if (lastEntry.textMessage?.sessionSuggestions?.quickResponses) {
        return lastEntry.textMessage.sessionSuggestions.quickResponses || [];
      }
    }
    return [];
  }, [entries, localEntries]);

  return (
    <Box
      sx={{
        borderRadius: isMobile ? "0" : "16px 0 0 0",
        height: "100%",
      }}
    >
      <ScrollableActivityPanel
        headerPanel={
          !isMobile && (
            <ContactSection
              feedFocusState={feedFocusState}
              setFeedFocusState={(s) => {
                setFeedFocusState(s);
              }}
              familyRef={family.ref}
            />
          )
        }
        scrollEntries={scrollEntries}
        fetchScrollEntries={fetchMoreData}
        hasMoreScrollEntries={hasMore}
        scrollRef={scrollRef}
        aboveFooterPanel={<ActivityFeedFamilyStateWarning family={family} />}
        footerPanel={
          <CompositionSection
            addFeedEntry={addFeedEntry}
            removeFeedEntry={(ref, med) => {
              const clean = (l: FeedEntry[]) =>
                l.filter((x) => !(x.ref === ref && x.medium === med));
              setLocalEntries(clean);
              setEntries(clean);
            }}
            family={family}
            groupThreads={groupThreads}
            accountMap={accountMap}
            feedFocusState={feedFocusState}
            setFeedFocusState={setFeedFocusState}
            primaryAdvisor={primaryAdvisor}
            advisorPermissions={advisorPermissions}
            quickResponseSuggestions={quickResponseSuggestions}
            actionCallbacks={communicationCallbacks}
            setCachedRecipient={setCachedRecipient}
          />
        }
      />
      <AttachmentDialog
        attachment={openAttachment}
        onClose={() => setOpenAttachment(null)}
      />
      <BookmarkToTaskDialog
        familyRef={family.ref}
        open={!!bookmarkEntry}
        onClose={() => {
          setBookmarkEntry(null);
          refresh();
        }}
        entry={bookmarkEntry ? fromFeedEntry(bookmarkEntry) : null}
      />
    </Box>
  );
};
